/**
 * This code was AUTOGENERATED using the codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type IAccountMeta,
  type IInstruction,
  type IInstructionWithAccounts,
  type IInstructionWithData,
  type ReadonlyAccount,
} from '@solana/kit';
import { LIGHTHOUSE_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';
import {
  LogLevel,
  getLogLevelDecoder,
  getLogLevelEncoder,
  getStakeAccountAssertionDecoder,
  getStakeAccountAssertionEncoder,
  type LogLevelArgs,
  type StakeAccountAssertion,
  type StakeAccountAssertionArgs,
} from '../types';

export const ASSERT_STAKE_ACCOUNT_DISCRIMINATOR = 11;

export function getAssertStakeAccountDiscriminatorBytes() {
  return getU8Encoder().encode(ASSERT_STAKE_ACCOUNT_DISCRIMINATOR);
}

export type AssertStakeAccountInstruction<
  TProgram extends string = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
  TAccountTargetAccount extends string | IAccountMeta<string> = string,
  TRemainingAccounts extends readonly IAccountMeta<string>[] = [],
> = IInstruction<TProgram> &
  IInstructionWithData<Uint8Array> &
  IInstructionWithAccounts<
    [
      TAccountTargetAccount extends string
        ? ReadonlyAccount<TAccountTargetAccount>
        : TAccountTargetAccount,
      ...TRemainingAccounts,
    ]
  >;

export type AssertStakeAccountInstructionData = {
  discriminator: number;
  logLevel: LogLevel;
  assertion: StakeAccountAssertion;
};

export type AssertStakeAccountInstructionDataArgs = {
  logLevel?: LogLevelArgs;
  assertion: StakeAccountAssertionArgs;
};

export function getAssertStakeAccountInstructionDataEncoder(): Encoder<AssertStakeAccountInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', getU8Encoder()],
      ['logLevel', getLogLevelEncoder()],
      ['assertion', getStakeAccountAssertionEncoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: ASSERT_STAKE_ACCOUNT_DISCRIMINATOR,
      logLevel: value.logLevel ?? LogLevel.Silent,
    })
  );
}

export function getAssertStakeAccountInstructionDataDecoder(): Decoder<AssertStakeAccountInstructionData> {
  return getStructDecoder([
    ['discriminator', getU8Decoder()],
    ['logLevel', getLogLevelDecoder()],
    ['assertion', getStakeAccountAssertionDecoder()],
  ]);
}

export function getAssertStakeAccountInstructionDataCodec(): Codec<
  AssertStakeAccountInstructionDataArgs,
  AssertStakeAccountInstructionData
> {
  return combineCodec(
    getAssertStakeAccountInstructionDataEncoder(),
    getAssertStakeAccountInstructionDataDecoder()
  );
}

export type AssertStakeAccountInput<
  TAccountTargetAccount extends string = string,
> = {
  /** Target account to be asserted */
  targetAccount: Address<TAccountTargetAccount>;
  logLevel?: AssertStakeAccountInstructionDataArgs['logLevel'];
  assertion: AssertStakeAccountInstructionDataArgs['assertion'];
};

export function getAssertStakeAccountInstruction<
  TAccountTargetAccount extends string,
  TProgramAddress extends Address = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
>(
  input: AssertStakeAccountInput<TAccountTargetAccount>,
  config?: { programAddress?: TProgramAddress }
): AssertStakeAccountInstruction<TProgramAddress, TAccountTargetAccount> {
  // Program address.
  const programAddress = config?.programAddress ?? LIGHTHOUSE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    targetAccount: { value: input.targetAccount ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  const instruction = {
    accounts: [getAccountMeta(accounts.targetAccount)],
    programAddress,
    data: getAssertStakeAccountInstructionDataEncoder().encode(
      args as AssertStakeAccountInstructionDataArgs
    ),
  } as AssertStakeAccountInstruction<TProgramAddress, TAccountTargetAccount>;

  return instruction;
}

export type ParsedAssertStakeAccountInstruction<
  TProgram extends string = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly IAccountMeta[] = readonly IAccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Target account to be asserted */
    targetAccount: TAccountMetas[0];
  };
  data: AssertStakeAccountInstructionData;
};

export function parseAssertStakeAccountInstruction<
  TProgram extends string,
  TAccountMetas extends readonly IAccountMeta[],
>(
  instruction: IInstruction<TProgram> &
    IInstructionWithAccounts<TAccountMetas> &
    IInstructionWithData<Uint8Array>
): ParsedAssertStakeAccountInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 1) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = instruction.accounts![accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      targetAccount: getNextAccount(),
    },
    data: getAssertStakeAccountInstructionDataDecoder().decode(
      instruction.data
    ),
  };
}
